import React, { useEffect, useState, useMemo, useCallback } from 'react';
import GlobalDialog from '../../../../components/mui/Dialogue/GlobalDialog';
import { CancelButton, LogButton, SaveButton } from '../../../../components/mui/Buttons/Buttons';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import MotionContainer from '../../../../components/FramerMotion/MotionContainer';
import { motion } from 'framer-motion';
import OutlinedInput from '@mui/material/OutlinedInput';
import { Icon } from '@iconify/react';
import { useSelector, useDispatch } from 'react-redux';
import { getAvailableBatch } from '../../../../redux/features/cartSlice';
import {
  clearAllCities,
  clearAllStates,
  getAllCities,
  getAllCountries,
  getAllStates,
} from '../../../../redux/features/thirdPartySlice';

const ErrorMsg = React.memo(({ charLen }) =>
  charLen > 1800 ? <Alert severity="error">Character limit exceeded...!</Alert> : null
);

const RemarkPopup = ({ item, handleInputChange, handleClose, update, paid }) => {
  const { loading, availableBatchList } = useSelector((state) => state.cart);
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  // --------------------------------------------------------------------

  const countries = useSelector((state) => state.thirdParty.allCountries.data);
  const states = useSelector((state) => state.thirdParty.allStates.data);
  const cities = useSelector((state) => state.thirdParty.allCities.data);

  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');

  // -----------------------------------------------------------------

  const dispatch = useDispatch();
  const { classes } = useSelector((state) => state.cro);
  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState(false);

  const names = useMemo(
    () => ['level 1', 'level 2', 'level 3', 'level 4', 'level 5', 'Basic', 'Intermediate', 'Advanced'],
    []
  );

  const menuProps = useMemo(() => ({ PaperProps: { style: { maxHeight: 48 * 4.5 + 8, width: 250 } } }), []);

  const [data, setData] = useState({
    levelOpted: { target: { value: [] } },
    booksAndPublication: { target: { value: '' } },
    preferredChapter: { target: { value: '' } },
    remarks: { target: { value: '' } },
    courseLevelStatus: { target: { value: item?.courseLevelStatus } },
    // currentClass: { target: { value: item?.currentClass || '' } },
    subjectsAndSession: { target: { value: item?.subjectsAndSession || '' } },
    // Batch removed from cart (Option B): courseBatchId no longer collected/sent.
    // courseBatchId: { target: { value: item?.courseBatchId || '' } },
    // --------------------------------------------------------------
    country: { target: { value: item?.country || '' } },
    state: { target: { value: item?.state || '' } },
    city: { target: { value: item?.city || '' } },
    isRetention: { target: { value: item?.isRetention || '' } },
    currentAddress: { target: { value: item?.currentAddress || '' } },
  });

  // -----------------------------------------------------------
  // Country / state / city lists come from our own backend through thirdPartySlice, which looks states and cities
  // up by id. The form still stores names, so each level finds the selected item's id in the list above it.
  // Fetch countries (on mount)
  useEffect(() => {
    dispatch(getAllCountries());
  }, [dispatch]);

  // Fetch states when country changes (or once the country list arrives)
  useEffect(() => {
    const selected = countries.find((c) => c.name === data.country.target.value);
    if (selected) dispatch(getAllStates(selected.id));
    else dispatch(clearAllStates());
    dispatch(clearAllCities());
  }, [data.country.target.value, countries, dispatch]);

  // Fetch cities when state changes (or once the state list arrives)
  useEffect(() => {
    const selected = states.find((s) => s.name === data.state.target.value);
    if (selected) dispatch(getAllCities(selected.id));
    else dispatch(clearAllCities());
  }, [data.state.target.value, states, dispatch]);
  // ----------------------------------------------------------

  // Batch removed from cart (Option B): no longer fetch selectable batches.
  // useEffect(() => {
  //   if (open && item.batchAvailability === 1) dispatch(getAvailableBatch(item.id));
  // }, [open, item.id, dispatch]);

  useEffect(() => {
    if (item) {
      setData({
        levelOpted: { target: { value: item.levelOpted || [] } },
        booksAndPublication: {
          target: {
            value: item.projectId !== 1 ? 'Nil' : item.booksAndPublication || '',
          },
        },
        preferredChapter: {
          target: {
            value: item.projectId !== 1 ? 'Nil' : item.preferredChapter || '',
          },
        },
        remarks: { target: { value: item.remarks || '' } },
        courseLevelStatus: { target: { value: item.courseLevelStatus } },
        // currentClass: { target: { value: item.currentClass || '' } },
        subjectsAndSession: {
          target: { value: item.subjectsAndSession || '' },
        },
        // Batch removed from cart (Option B): courseBatchId no longer collected/sent.
        // courseBatchId: { target: { value: item?.courseBatchId || '' } },
        // -----------------------------------------------------------------
        country: { target: { value: item?.country || '' } },
        state: { target: { value: item?.state || '' } },
        city: { target: { value: item?.city || '' } },
        isRetention: { target: { value: item?.isRetention || 0 } },
        currentAddress: { target: { value: item?.currentAddress || '' } },
      });
    }
  }, [item]);

  const handleFieldChange = useCallback((field, value) => {
    setData((prev) => ({ ...prev, [field]: { target: { value } } }));
  }, []);

  const handleSubmit = () => {
    const d = data;
    const isInvalid =
      !d.booksAndPublication.target.value ||
      // !d.subjectsAndSession.target.value ||
      // (d.courseLevelStatus.target.value &&
      //   d.levelOpted.target.value.length === 0) ||
      !d.currentAddress.target.value ||
      !d.preferredChapter.target.value ||
      !d.remarks.target.value;
    // Batch removed from cart (Option B): batch selection no longer required.
    // (item.batchAvailability === 1 && !d.courseBatchId.target.value);
    //  ||
    // !d.country.target.value ||
    // !d.state.target.value ||
    // !d.city.target.value;

    if (isInvalid) {
      enqueueSnackbar('Please fill all fields ...!', { variant: 'error' });
      return;
    }

    setOpen(false);
    handleClose();
    Object.entries(d).forEach(([key, val]) => {
      handleInputChange(val, item.id, key);
    });

    enqueueSnackbar('Remarks Added Successfully...!', { variant: 'success' });
  };

  const charValidation = useMemo(
    () => ({
      booksAndPublication: data.booksAndPublication.target.value.length > 1800,
      preferredChapter: data.preferredChapter.target.value.length > 1800,
      remarks: data.remarks.target.value.length > 1800,
      subjectsAndSession: data.subjectsAndSession.target.value.length > 1800,
    }),
    [data]
  );

  return (
    <GlobalDialog
      title="Remarks"
      open={open}
      setOpen={setOpen}
      closeButton
      maxWidth="sm"
      fullWidth
      popupButton={
        <LogButton size="small" action={() => setOpen(true)}>
          {update || paid ? 'View Remarks' : 'Add Remarks'}
        </LogButton>
      }
      content={
        open && (
          <Box>
            {/* ---------------------------batch---------------------------- */}
            {/* Batch removed from cart (Option B): batch picker disabled. */}
            {/* {item.batchAvailability === 1 ? (
              <Stack direction={'column'} sx={{ mb: 2 }}>
                <Autocomplete
                  options={availableBatchList}
                  getOptionLabel={(option) => option.display || ''}
                  value={
                    availableBatchList.find((item) => item.courseBatchID === data.courseBatchId.target.value) || null
                  }
                  onChange={(event, newValue) => {
                    handleFieldChange('courseBatchId', newValue ? newValue.courseBatchID : null);
                    // setSelectedBatchId(
                    //   newValue ? newValue.courseBatchID : null
                    // );
                  }}
                  renderInput={(params) => <TextField {...params} label="Select Course Batch" variant="outlined" />}
                />
              </Stack>
            ) : null} */}

            {/* ---------------------------------------------------- */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              {/* Country Dropdown */}
              {/* <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Country</InputLabel>
                <Select
                  value={data.country.target.value}
                  label="Country"
                  onChange={(e) => {
                    handleFieldChange('country', e.target.value);
                    setSelectedCountry(e.target.value);
                    setSelectedState('');
                    setSelectedCity('');
                  }}
                >
                  {countries.map((c) => (
                    <MenuItem key={c.id} value={c.name}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl> */}

              {/* State Dropdown */}
              {/* <FormControl sx={{ minWidth: 200 }} disabled={!states.length}>
                <InputLabel>State</InputLabel>
                <Select
                  value={data.state.target.value}
                  label="State"
                  onChange={(e) => {
                    handleFieldChange('state', e.target.value);
                    setSelectedState(e.target.value);
                    setSelectedCity('');
                  }}
                >
                  {states.map((s) => (
                    <MenuItem key={s.id} value={s.name}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl> */}

              {/* City Dropdown */}
              {/* <FormControl sx={{ minWidth: 200 }} disabled={!cities.length}>
                <InputLabel>City</InputLabel>
                <Select
                  value={data.city.target.value}
                  label="City"
                  onChange={(e) => {
                    handleFieldChange('city', e.target.value);
                    setSelectedCity(e.target.value);
                  }}
                >
                  {cities.map((city) => (
                    <MenuItem key={city.id} value={city.name}>
                      {city.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl> */}
            </Box>
            {/* -------------------------------------------------------------- */}

            <Stack direction="column" spacing={2}>
              <TextField
                label="Current Address"
                multiline
                fullWidth
                rows={3}
                value={data.currentAddress.target.value}
                onChange={(e) => handleFieldChange('currentAddress', e.target.value)}
              />
              {/* <MotionContainer delay={0.1}>
                <FormControl fullWidth>
                  <InputLabel>Current Class</InputLabel>
                  <Select
                    label="Current Class"
                    value={data.currentClass.target.value}
                    onChange={(e) => handleFieldChange('currentClass', e.target.value)}
                  >
                    {classes.map((cls) => (
                      <MenuItem key={cls.classId} value={cls.classId}>
                        {cls.className}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </MotionContainer> */}
              {/* --------------------------------------- */}

              <MotionContainer delay={0.1}>
                <FormControl fullWidth>
                  <InputLabel>Retention Status</InputLabel>
                  <Select
                    label="Retention Status"
                    value={data.isRetention.target.value}
                    onChange={(e) => handleFieldChange('isRetention', e.target.value)}
                  >
                    {[
                      { id: 0, name: 'No Retention' },
                      { id: 1, name: 'Up Sell', disabled: true },
                      { id: 2, name: 'Cross Sell' },
                    ].map((item) => (
                      <MenuItem key={item.id} value={item.id} disabled={item.disabled}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Use Admission Register to add an up-sell for an enrolled course.</FormHelperText>
                </FormControl>
              </MotionContainer>

              <TextField
                label="Remarks"
                multiline
                fullWidth
                rows={3}
                value={data.remarks.target.value}
                onChange={(e) => handleFieldChange('remarks', e.target.value)}
              />
              <ErrorMsg charLen={data.remarks.target.value.length} />

              {/* {(item?.courseLevelStatus && (
                <MotionContainer>
                  <FormControl fullWidth>
                    <InputLabel>Level Opted</InputLabel>
                    <Select
                      multiple
                      value={data.levelOpted.target.value}
                      onChange={(e) =>
                        handleFieldChange(
                          'levelOpted',
                          typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
                        )
                      }
                      input={<OutlinedInput label="Level Opted" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((val, i) => (
                            <motion.div
                              key={i}
                              initial={{ opacity: 0, x: -80 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ duration: 0.4 }}
                            >
                              <Chip
                                size="small"
                                sx={{
                                  background: `linear-gradient(to bottom, #0089d0 0%, #0089d0 10%, #a125c2 100%)`,
                                  color: 'white',
                                }}
                                icon={<Icon color="white" icon="clarity:language-line" />}
                                label={val}
                              />
                            </motion.div>
                          ))}
                        </Box>
                      )}
                      MenuProps={menuProps}
                    >
                      {names.map((name) => (
                        <MenuItem key={name} value={name}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </MotionContainer>
              )) ||
                null} */}

              {/* {(item.projectId === 1 && (
                <>
                  <TextField
                    label="Books And Publication"
                    multiline
                    fullWidth
                    rows={3}
                    value={data.booksAndPublication.target.value}
                    onChange={(e) => handleFieldChange('booksAndPublication', e.target.value)}
                  />
                  <ErrorMsg charLen={data.booksAndPublication.target.value.length} />

                  <TextField
                    label="Preferred Chapter"
                    multiline
                    fullWidth
                    rows={3}
                    value={data.preferredChapter.target.value}
                    onChange={(e) => handleFieldChange('preferredChapter', e.target.value)}
                  />
                  <ErrorMsg charLen={data.preferredChapter.target.value.length} />
                </>
              )) ||
                null} */}

              {/* <TextField
                label="Subjects And Session"
                multiline
                fullWidth
                rows={3}
                value={data.subjectsAndSession.target.value}
                onChange={(e) => handleFieldChange('subjectsAndSession', e.target.value)}
              />
              <ErrorMsg charLen={data.subjectsAndSession.target.value.length} /> */}
            </Stack>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: 1 }}>
              <Stack direction="row" spacing={1}>
                <CancelButton size="small" action={() => setOpen(false)}>
                  Cancel
                </CancelButton>
              </Stack>
              <SaveButton
                size="small"
                disabled={paid || Object.values(charValidation).some(Boolean)}
                action={handleSubmit}
              >
                Save
              </SaveButton>
            </Box>
          </Box>
        )
      }
    />
  );
};

export default RemarkPopup;
